<template>
    <v-container fluid class="penalties-page secondary-font">
        <div class="penalties-header">
            <span class="penalties-header__icon custom-orange">
                <v-icon color="white">mdi-whistle-outline</v-icon>
            </span>
            <h1 class="penalties-header__title">Ποινολόγιο</h1>
            <v-select
                v-model="season"
                color="orange"
                item-color="orange"
                label="Σεζόν"
                :items="seasons"
                item-text="name"
                return-object
                dense
                outlined
                hide-details
                prepend-inner-icon="mdi-calendar"
                class="penalties-header__season"
                @change="onSeasonChange"
            />
        </div>
        <v-row>
            <v-col cols="12">
                <v-data-table
                    :headers="headers"
                    :items="penalties"
                    :loading="loading"
                    loading-text="Φόρτωση..."
                    no-data-text="Δεν υπάρχουν ποινές για αυτή τη σεζόν"
                    class="penalties-table no-global-table-hover elevation-1 rounded-lg"
                    hide-default-footer
                    :mobile-breakpoint="0"
                    :items-per-page="-1"
                >
                    <template v-slot:item.player="{ item }">
                        <div
                            class="player-cell"
                            role="button"
                            tabindex="0"
                            @click="$router.push({ name: 'Player', params: { id: item.playerId } })"
                            @keydown.enter="$router.push({ name: 'Player', params: { id: item.playerId } })"
                        >
                            <span class="thumb">
                                <v-img
                                    :src="item.playerImage ? `${assetsUrl}/${item.playerImage}` : defaultPlayerImg"
                                    contain
                                    class="thumb__img"
                                />
                            </span>
                            <span class="font-weight-bold link-name">{{ item.playerSurname }} {{ item.playerName }}</span>
                        </div>
                    </template>
                    <template v-slot:item.team="{ item }">
                        <div
                            v-if="item.teamId"
                            class="player-cell"
                            role="button"
                            tabindex="0"
                            @click="$router.push({ name: 'Team', params: { id: item.teamId } })"
                            @keydown.enter="$router.push({ name: 'Team', params: { id: item.teamId } })"
                        >
                            <span v-if="item.teamLogo" class="thumb">
                                <v-img :src="`${assetsUrl}/${item.teamLogo}`" contain class="thumb__img" />
                            </span>
                            <span v-if="!$vuetify.breakpoint.mobile" class="link-name">{{ item.teamName }}</span>
                        </div>
                    </template>
                    <template v-slot:item.ban="{ item }">
                        <span class="ban-badge" :class="item.permanent ? 'ban-badge--permanent' : 'custom-orange'">
                            <v-icon x-small color="white">{{ item.permanent ? 'mdi-cancel' : 'mdi-timer-sand' }}</v-icon>
                            {{ item.permanent ? 'Οριστικός αποκλεισμός' : `${item.matches} ${item.matches === 1 ? 'αγώνας' : 'αγώνες'}` }}
                        </span>
                    </template>
                    <template v-slot:item.reason="{ item }">
                        <span class="reason">{{ item.reason }}</span>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import moment from 'moment'
    import penaltyService from '@/services/penalty'
    import seasonService from '@/services/season'

    export default {
        name: 'Penalties',
        data() {
            return {
                seasons: [],
                season: null,
                penalties: [],
                loading: false,
                assetsUrl: 'https://storage.googleapis.com',
                defaultPlayerImg: require('@/assets/default_player.png')
            }
        },
        computed: {
            headers() {
                const headers = [
                    { text: 'Παίκτης', value: 'player', sortable: false },
                    { text: 'Ομάδα', value: 'team', sortable: false },
                    { text: 'Ποινή', value: 'ban', sortable: false },
                    { text: 'Έναρξη', value: 'startDate', sortable: false }
                ]
                if (!this.$vuetify.breakpoint.mobile) {
                    headers.push({ text: 'Αιτιολογία', value: 'reason', sortable: false })
                }
                return headers
            }
        },
        created() {
            this.fetchSeasons()
        },
        methods: {
            fetchSeasons() {
                seasonService.getSeasons(1, 100).then(resp => {
                    this.seasons = resp.data.content
                    const routeSeasonId = Number.parseInt(this.$route.query.seasonId, 10)
                    this.season = this.seasons.find(element => element.id === routeSeasonId) ||
                        this.seasons.find(element => moment().isBetween(moment(element.startDate), moment(element.endDate))) ||
                        this.seasons[0] ||
                        null
                    this.fetchPenalties()
                })
            },
            onSeasonChange() {
                if (this.season && String(this.season.id) !== this.$route.query.seasonId) {
                    this.$router.replace({ query: { ...this.$route.query, seasonId: String(this.season.id) } })
                }
                this.fetchPenalties()
            },
            fetchPenalties() {
                if (!this.season) {
                    this.penalties = []
                    return
                }
                this.loading = true
                penaltyService.getPenaltiesBySeason(this.season.id).then(resp => {
                    this.penalties = resp.data
                }).finally(() => {
                    this.loading = false
                })
            }
        }
    }
</script>

<style scoped>
.penalties-page {
    max-width: 1200px;
    margin: 0 auto;
}

/* ---------- Header ---------- */
.penalties-header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
    padding: 14px 18px;
    background: #fff;
    border-radius: 12px;
    border-top: 4px solid #ff7133;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
}

.penalties-header__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 10px;
}

.penalties-header__title {
    flex: 1 1 auto;
    font-size: 1.9rem;
    font-weight: 800;
    line-height: 1.2;
    text-align: left;
}

.penalties-header__season {
    flex: 0 0 330px;
    font-family: Ubuntu, Arial, sans-serif;
}

/* ---------- Table (same look as the standings) ---------- */
.penalties-table {
    overflow: hidden;
    font-family: Ubuntu, Arial, sans-serif;
}

.penalties-table ::v-deep .v-data-table__wrapper table > thead > tr > th {
    background: #0d0d0f !important;
    color: #fff !important;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: nowrap;
    border-bottom: 3px solid #ff7133 !important;
}

.penalties-table ::v-deep .v-data-table__wrapper table > tbody > tr > td {
    height: 56px;
    background: #fff;
}

.penalties-table ::v-deep .v-data-table__wrapper table > tbody > tr:nth-child(even) > td {
    background: #f7f7f8 !important;
}

.penalties-table ::v-deep .v-data-table__wrapper table > tbody > tr:hover > td {
    background: #fff3ec !important;
}

.penalties-table ::v-deep .v-data-table__wrapper table > tbody > tr:hover > td:first-child {
    box-shadow: inset 4px 0 0 #ff7133;
}

.player-cell {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
}

.player-cell:hover .link-name {
    color: #ef4444;
}

.thumb {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 2px;
    border-radius: 6px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.thumb__img {
    width: 100%;
    height: 100%;
}

.ban-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 6px;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 700;
    white-space: nowrap;
}

.ban-badge--permanent {
    background: #c62828;
}

.reason {
    display: block;
    max-width: 360px;
    color: rgba(0, 0, 0, 0.7);
    white-space: normal;
}

@media (max-width: 600px) {
    .penalties-header__title {
        font-size: 1.45rem;
    }

    .penalties-header__season {
        flex: 1 1 100%;
    }
}
</style>
